import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { AstraWidget } from "./AstraWidget";
import { useT, LangSwitcher } from "../../lib/i18n";
import { OFFERS, CITIES, SECTOR_LINKS, CONTACT } from "../../content/site";
import { ZONE_LINKS } from "../../content/zones";
import { UNIVERSES } from "../../content/universes";
import { CallbackButton } from "./CallbackButton";
import { ExitIntent } from "./ExitIntent";
import { Pixels } from "./Pixels";


const NAV = [
  ["Produits", "/produits", "nav-produits"],
  ["Événements", OFFERS.evenement.path, "nav-evenement"],
  ["L'atelier", "/atelier", "nav-atelier"],
];

function Header() {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 bg-[#FBF9F5]/90 backdrop-blur-md border-b border-[#E8E2D8] no-print">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 shrink-0">
          <span className="font-display text-2xl tracking-[0.18em] font-semibold">KOUOH</span>
          <span className="hidden sm:inline font-mono-k text-[10px] tracking-[0.2em] uppercase text-[#8C857B]">Atelier · Nice</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm" aria-label="Navigation principale">
          {NAV.map(([l, to, id]) => (
            to === "/produits" ? (
              <div key={to} className="relative group" data-testid="nav-produits-wrap">
                <NavLink to={to} data-testid={id} className={({ isActive }) => `transition-colors hover:text-[#C25E38] ${isActive ? "text-[#C25E38]" : "text-[#44403c]"}`}>{t(l)}</NavLink>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block z-50">
                  <div className="bg-[#FBF9F5] border border-[#E8E2D8] shadow-lg p-5 grid grid-cols-5 gap-6 w-[880px]" data-testid="nav-universes">
                    {UNIVERSES.map((u) => (
                      <div key={u.key}>
                        <Link to={`/produits?univers=${u.key}`} className="k-eyebrow !text-[0.62rem] hover:text-[#C25E38]" data-testid={`nav-univers-${u.key}`}>{t(u.label)}</Link>
                        <ul className="mt-2 grid gap-1">{u.families.map(([c, fl]) => <li key={c}><Link to={`/produits?univers=${u.key}&category=${c}`} className="text-xs text-[#57534E] hover:text-[#C25E38]">{t(fl)}</Link></li>)}</ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
            <NavLink key={to} to={to} data-testid={id} className={({ isActive }) => `transition-colors hover:text-[#C25E38] ${isActive ? "text-[#C25E38]" : "text-[#44403c]"}`}>{t(l)}</NavLink>
            )
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <Link to="/demande" data-testid="nav-demande" className="k-btn k-btn-sm">{t("Demander un devis")}</Link>
          <LangSwitcher />
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#E8E2D8] bg-[#FBF9F5] px-5 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {NAV.map(([l, to, id]) => <Link key={to} to={to} data-testid={`m-${id}`} className="font-display text-2xl">{t(l)}</Link>)}
          <Link to="/demande" className="k-btn mt-2" data-testid="m-nav-demande">{t("Demander un devis")}</Link>
          <LangSwitcher className="mt-3" />
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useT();
  const col = "flex flex-col gap-2 text-sm text-[#D6CEBF]";
  const a = "hover:text-[#FBF9F5] transition-colors";
  return (
    <footer className="bg-[#1C1917] text-[#FBF9F5] no-print">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-3">
          <p className="font-display text-3xl tracking-[0.18em]">KOUOH</p>
          <p className="font-display italic text-lg mt-3 text-[#D6CEBF]">Maison de personnalisation textile.</p>
          <p className="text-sm text-[#8C857B] mt-6 leading-relaxed">Atelier à Nice. Broderie, impression textile et sérigraphie pour la Côte d'Azur — {CITIES.join(", ")} — et Monaco.</p>
        </div>
        <div className={`md:col-span-2 ${col}`}>
          <span className="k-eyebrow mb-2">Offres</span>
          {Object.values(OFFERS).map((o) => <Link key={o.path} to={o.path} className={a}>{o.title}</Link>)}
        </div>
        <div className={`md:col-span-3 ${col}`}>
          <span className="k-eyebrow mb-2">Savoir-faire & secteurs</span>
          <Link to="/savoir-faire/broderie" className={a}>Broderie</Link>
          <Link to="/savoir-faire/impression-dtf" className={a}>Impression DTF</Link>
          <Link to="/savoir-faire/serigraphie" className={a}>Sérigraphie</Link>
          {SECTOR_LINKS.map(([to, l]) => <Link key={to} to={to} className={a}>{l}</Link>)}
        </div>
        <div className={`md:col-span-2 ${col}`}>
          <span className="k-eyebrow mb-2">Où nous intervenons</span>
          {ZONE_LINKS.slice(0, 9).map(([to, l]) => <Link key={to} to={to} className={a} data-testid={`footer-zone-${to.split("/").pop()}`}>{l}</Link>)}
          <Link to="/zones" className={`${a} underline underline-offset-4`} data-testid="footer-zones-all">{t("Toutes les villes du 06 →")}</Link>
          <span className="k-eyebrow mb-2 mt-4">Atelier</span>
          <Link to="/atelier" className={a}>L'atelier</Link>
          <Link to="/realisations" className={a} data-testid="footer-realisations">Réalisations</Link>
          <CallbackButton className={`${a} text-left inline-flex items-center gap-1.5`} />
          <Link to="/conseils" className={a} data-testid="footer-conseils">Conseils</Link>
          <Link to="/questions-frequentes" className={a}>Questions fréquentes</Link>
          <Link to="/espace-client" className={a} data-testid="footer-espace-client">Mon dossier client</Link>
          <Link to="/mentions-legales" className={a}>{t("Mentions légales")}</Link>
          <span className="k-eyebrow mb-2 mt-4">Contact</span>
          <a href={`mailto:${CONTACT.email}`} className={a} data-testid="footer-email">{CONTACT.email}</a>
          {CONTACT.phone && <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className={a} data-testid="footer-phone">{CONTACT.phone}</a>}
          <span className="text-sm text-[#8C857B]">{CONTACT.address}</span>
          <Link to="/confidentialite" className={a}>Confidentialité</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-3 text-xs text-[#8C857B] font-mono-k tracking-widest">
        <span>© {new Date().getFullYear()} ATELIER KOUOH — NICE</span>
        <Link to="/admin" className="inline-flex items-center gap-1.5 hover:text-[#FBF9F5] transition-colors" data-testid="footer-admin-link"><Lock size={11} /> ESPACE ÉQUIPE</Link>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const { t } = useT();
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:p-3 focus:bg-white z-50">Aller au contenu</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <AstraWidget />
      <ExitIntent />
      <Pixels />
    </div>
  );
}
